/**
 * Pane theme: the design system's light or dark tokens, picked from Word's
 * own theme, plus the white-label brand colour.
 *
 * # DECISION: follow Word's theme, no in-pane switch.
 * # WHY: the design's dark board ("Spine · Word dark theme") shows the pane
 * #      matching Word; a pane that stays white inside a dark Word is the
 * #      thing the board exists to rule out.
 * # DATE: 2026-10-22
 */

import { brand } from "../config/brand";

type Theme = "light" | "dark";

/** Relative luminance of a #rrggbb colour, 0..1. Pure. */
export function luminance(hex: string): number {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return 1;
  const n = Number.parseInt(m[1], 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

/** Dark when Word's pane background is dark. Pure. */
export function themeForBackground(background: string | undefined | null): Theme {
  if (!background) return "light";
  return luminance(background) < 0.2 ? "dark" : "light";
}

function officeBackground(): string | null {
  try {
    if (typeof Office === "undefined") return null;
    return Office.context?.officeTheme?.bodyBackgroundColor ?? null;
  } catch {
    return null;
  }
}

function forcedTheme(): Theme | null {
  if (typeof window === "undefined") return null;
  const t = new URLSearchParams(window.location.search).get("theme");
  return t === "dark" || t === "light" ? t : null;
}

export function applyTheme(root: HTMLElement = document.documentElement): Theme {
  const theme = forcedTheme() ?? themeForBackground(officeBackground());
  root.setAttribute("data-theme", theme);
  // White-label brands keep their own colour; Asthra uses the design system's.
  if (brand.key !== "asthra") {
    root.style.setProperty("--brand", brand.colorPrimary);
    root.style.setProperty("--brand-strong", brand.colorPrimary);
    root.style.setProperty("--brand-soft", brand.colorPrimaryLight);
    root.style.setProperty("--brand-wash", brand.colorPrimaryLight);
    root.style.setProperty("--focus", brand.colorPrimaryMid);
  }
  return theme;
}

/** Apply now and again whenever Word switches theme. */
export function installTheme(): void {
  applyTheme();
  try {
    const ev = typeof Office !== "undefined" ? Office.EventType?.OfficeThemeChanged : undefined;
    if (ev && Office.context?.document?.addHandlerAsync) {
      Office.context.document.addHandlerAsync(ev, () => applyTheme());
    }
  } catch {
    // Older hosts have no theme-change event; the theme is read at load.
  }
}

/**
 * Fluent theme whose tokens point at the design-system variables, so any
 * Fluent control still on screen follows the palette, fonts and Word theme.
 */
export function asthraFluentTheme<T extends Record<string, unknown>>(base: T): T {
  return {
    ...base,
    fontFamilyBase: "var(--font-sans)",
    fontFamilyMonospace: "var(--font-mono)",
    colorNeutralForeground1: "var(--ink)",
    colorNeutralForeground2: "var(--ink-muted)",
    colorNeutralForeground3: "var(--ink-subtle)",
    colorNeutralForeground4: "var(--ink-subtle)",
    colorNeutralBackground1: "var(--surface)",
    colorNeutralBackground1Hover: "var(--surface-sunken)",
    colorNeutralBackground2: "var(--surface-sunken)",
    colorNeutralBackground3: "var(--surface-sunken)",
    colorNeutralStroke1: "var(--line-strong)",
    colorNeutralStroke2: "var(--line)",
    colorBrandBackground: "var(--brand)",
    colorBrandBackgroundHover: "var(--brand-strong)",
    colorBrandBackgroundPressed: "var(--brand-strong)",
    colorBrandForeground1: "var(--brand-strong)",
    colorBrandForegroundLink: "var(--brand-strong)",
    colorBrandStroke1: "var(--brand)",
    colorCompoundBrandStroke: "var(--brand)",
    colorCompoundBrandBackground: "var(--brand)",
    colorNeutralForegroundOnBrand: "var(--on-brand)",
    borderRadiusMedium: "var(--radius-md)",
  };
}
